/**
 * The front's lofi station (lane C0-LOFI, docs/research/C0_LOFI.md): six calm, original tracks the login plays one
 * after another (front/playlist.ts), crossfading. Each is a guzheng lead over a guzheng arpeggio, a held flute or
 * erhu line answering the lead in the second half of each phrase, a soft drone, and a light "boom-tap" of drum and
 * wooden fish that drops out for one section (the breakdown) so the loop breathes. Five-note lines written for this
 * game; nothing sampled, nothing copied.
 *
 * Form: a 2-bar intro, then A B A2 B (8 bars each); A2 is A over a half-time arpeggio without the beat.
 *
 * The leads use the zheng's own idiom (lane C0-FRONT-AUDIO, docs/research/GUZHENG_SOUND.md): a 刮奏 sweep
 * (g / G) into each section, 摇指 tremolo (%) on the held note that ends the first phrase, 按 / 绰 / 注 bends
 * (^ / \) and 揉 vibrato (~) on phrase ends. The notes and rhythms are the original ones.
 */
import { arp, drone, rep } from '../compose';
import { registerTrack } from '../registry';
import type { Instrument, TrackDef } from '../types';
import type { Key, Mode } from '../theory';

interface Lofi {
  id: string;
  order: number;
  title: string;
  mood: string;
  key: Key;
  mode: Mode;
  bpm: number;
  seed: number;
  /** The answering held voice. */
  counter: Instrument;
  leadA: string;
  leadB: string;
  counterA: string;
  counterB: string;
  /** One root a bar for the arpeggio and the drone. */
  rootsA: string;
  rootsB: string;
  /** Arpeggio steps (8 eighths a bar). */
  pattern: readonly (number | null)[];
  /** A soft bed of one percussion letter each bar (waves, crickets, bowl, bells), or none. */
  bed?: string;
  /** A second plucked voice doubling the lead an octave down in B (qin, pipa). */
  double?: Instrument;
}

// Swung boom-bap: dotted-eighth + sixteenth shuffle, ghost hats on the off-beats, a wooden-fish rim.
const BEAT = `D_. 0__ d_ c_p 0_ D_ k_ c_p`;
const BEAT2 = `D_. 0__ d_ c_p D_ 0_ k_ d_p`;
const HALF: readonly (number | null)[] = [0, null, 4, null, 2, null, 5, null];

function lofi(o: Lofi): TrackDef {
  const beat = `@p ${rep(BEAT, 3)} | ${BEAT2} | ${rep(BEAT, 3)} | ${BEAT2}`;
  const bed = o.bed ? `@p ${rep(`${o.bed}---`, 8)}` : undefined;
  const sec = (
    lead: string,
    counter: string,
    roots: string,
    pattern: readonly (number | null)[],
    withBeat: boolean,
    dbl?: string,
  ) => ({
    bars: 8,
    parts: {
      lead: `@mp ${lead}`,
      counter,
      arp: `@p ${arp(roots, pattern)}`,
      pad: drone(roots, 4),
      ...(withBeat ? { beat } : {}),
      ...(bed ? { bed } : {}),
      ...(dbl ? { dbl } : {}),
    },
  });
  return registerTrack({
    id: o.id,
    order: o.order,
    title: o.title,
    mood: o.mood,
    use: 'หน้าแรก: สถานีเพลงสบาย ๆ (ฟังเพลงอย่างเดียว)',
    key: o.key,
    mode: o.mode,
    bpm: o.bpm,
    meter: 4,
    seed: o.seed,
    humanize: 0.55,
    gain: 0.9,
    parts: {
      lead: { inst: 'zheng', gain: 0.95, pan: -0.12, reverb: 0.45 },
      arp: { inst: 'zheng', gain: 0.5, pan: 0.18, reverb: 0.5 },
      counter: { inst: o.counter, gain: 0.6, pan: 0.25, reverb: 0.6 },
      pad: { inst: 'pad', gain: 0.45, reverb: 0.6 },
      beat: { inst: 'perc', gain: 0.26, pan: 0, reverb: 0.2 },
      bed: { inst: 'perc', gain: 0.14, pan: -0.3, reverb: 0.7 },
      ...(o.double ? { dbl: { inst: o.double, gain: 0.45, pan: -0.3, reverb: 0.55 } } : {}),
    },
    intro: ['I'],
    sections: {
      I: {
        bars: 2,
        parts: {
          arp: `@p ${arp(o.rootsA.split(/\s+/).slice(0, 2).join(' '), HALF)}`,
          pad: drone(o.rootsA.split(/\s+/).slice(0, 2).join(' '), 4),
        },
      },
      A: sec(o.leadA, o.counterA, o.rootsA, o.pattern, true),
      B: sec(
        o.leadB,
        o.counterB,
        o.rootsB,
        o.pattern,
        true,
        o.double
          ? `@p ${o.leadB.replace(/([1-7])([',]*)/g, (_, d: string, m: string) => d + shift(m, -1))}`
          : undefined,
      ),
      A2: sec(o.leadA, `@p ${o.counterB}`, o.rootsA, HALF, false),
    },
    form: ['A', 'B', 'A2', 'B'],
    loop: true,
  });
}

/** Octave marks moved by `n` (one `,` more for −1). */
function shift(marks: string, n: number): string {
  let o = 0;
  for (const c of marks) o += c === "'" ? 1 : -1;
  o += n;
  return (o > 0 ? "'" : ',').repeat(Math.abs(o));
}

// 1. ศาลาริมทะเลสาบ: dawn over the lake pavilion; xiao answers.
lofi({
  id: 'lofiLake',
  order: 60,
  title: 'รุ่งอรุณศาลากลางน้ำ',
  mood: 'สงบ อบอุ่น ยามเช้า',
  key: 'D',
  mode: 'gong',
  bpm: 64,
  seed: 6001,
  counter: 'xiao',
  leadA: `3_g 5_ 6 5- | 3_ 2_ 1 2/- | 3 5_ 6_ 1' 6\\ | 5%--- | 6_ 1'_ 2' 1'_ 6_ 5 | 3. 2_ 3 5~ | 2_ 3_ 2_ 1_ 6, 1/ | 1~---`,
  leadB: `6g- 5 6 | 1'- 6 5\\ | 3_ 5_ 6_ 5_ 3 2 | 3%--- | 5- 3 2/ | 1_ 2_ 3 2 1 | 6,- 1 2~ | 1~---`,
  counterA: `0--- | 0--- | 0--- | 0- 5, 6, | 1- 2 3 | 2--- ; | 1_ 2_ 1 6,- | 5,--- ;`,
  counterB: `3--- | 5--- | 6- 5- | 3--- ; | 2--- | 3- 1- | 6,--- | 1--- ;`,
  rootsA: `1, 6,, 2, 5,, 1, 3, 2, 1,`,
  rootsB: `6,, 1, 2, 3, 5,, 6,, 5,, 1,`,
  pattern: [0, 2, 4, 2, 5, 4, 2, 4],
  bed: 'w',
});

// 2. น้ำตกเขาหยก: bright day by the falls; erhu answers.
lofi({
  id: 'lofiFalls',
  order: 61,
  title: 'ละอองน้ำตกเขาหยก',
  mood: 'สดชื่น โปร่ง กลางวัน',
  key: 'E',
  mode: 'zhi',
  bpm: 72,
  seed: 6002,
  counter: 'erhu',
  leadA: `5_g 6_ 1' 2'- | 1'_ 6_ 5 6~- | 3 5 6_ 5_ 3 | 2%--- | 3_ 5_ 6 1' 6\\ | 5. 3_ 2 3/ | 5_ 6_ 5_ 3_ 2 1 | 5,~---`,
  leadB: `1'G- 2' 1' | 6_ 5_ 6 5- | 3 2 3_ 5_ 6 | 5%--- | 6 5 3 2\\ | 1_ 2_ 3 2/- | 3_ 2_ 1 6,- | 5,~---`,
  counterA: `0--- | 0--- | 0--- | 0- 5, 6, | 1--- | 2- 3- | 5--- ; | 3--- ;`,
  counterB: `5--- | 3--- | 2- 3- | 5--- ; | 6--- | 5- 3- | 2--- | 5,--- ;`,
  rootsA: `5,, 3, 6,, 2, 3, 5,, 6,, 5,,`,
  rootsB: `1, 6,, 2, 5,, 6,, 1, 2, 5,,`,
  pattern: [0, 1, 2, 4, 5, 4, 2, 1],
  bed: 'w',
  double: 'pipa',
});

// 3. โคมแดงยามราตรี: the night market; xiao, crickets.
lofi({
  id: 'lofiLantern',
  order: 62,
  title: 'โคมแดงยามราตรี',
  mood: 'อบอุ่น ครึ้มใจ กลางคืน',
  key: 'A',
  mode: 'yu',
  bpm: 60,
  seed: 6003,
  counter: 'xiao',
  leadA: `6,g 1_ 2_ 3^- | 2_ 1_ 6, 5,- | 6, 3 2_ 1_ 2 | 6,%--- | 3 5_ 6_ 5 3\\ | 2. 1_ 2 3~ | 1_ 2_ 1_ 6,_ 5, 3, | 6,~---`,
  leadB: `6G- 5 3\\ | 5_ 6_ 1' 6- | 5 3 2_ 3_ 5 | 3%--- | 2 3 5 3/ | 2_ 1_ 6, 1- | 2_ 3_ 2 1 6,~ | 6,~---`,
  counterA: `0--- | 0--- | 0--- | 0- 3, 5, | 6,--- | 1- 2- | 3--- ; | 6,--- ;`,
  counterB: `1--- | 3--- | 2- 1- | 6,--- ; | 5,--- | 6,- 1- | 2--- | 6,--- ;`,
  rootsA: `6,, 1, 2, 6,, 3, 5,, 1, 6,,`,
  rootsB: `1, 5,, 2, 3, 2, 6,, 5,, 6,,`,
  pattern: [0, 2, 3, 2, 4, 3, 2, 3],
  bed: 'n',
});

// 4. กลีบบ๊วยริมสะพาน: an afternoon of falling petals; dizi answers.
lofi({
  id: 'lofiBlossom',
  order: 63,
  title: 'กลีบบ๊วยริมสะพาน',
  mood: 'สดใส อ่อนโยน บ่าย',
  key: 'G',
  mode: 'gong',
  bpm: 68,
  seed: 6004,
  counter: 'dizi',
  leadA: `1_g 2_ 3 5 3 | 2- 1 6,\\ | 5, 6, 1_ 2_ 3 | 2%--- | 3_ 5_ 6 5_ 3_ 2 | 1- 2 3/ | 5_ 3_ 2_ 1_ 6, 2 | 1~---`,
  leadB: `5_g 6_ 1' 6 5 | 6~- 5 3 | 2_ 3_ 5 3_ 2_ 1 | 2%--- | 3 2 1 6,\\ | 5,- 6, 1/ | 2_ 3_ 2_ 1_ 6,_ 5,_ 6, | 1~---`,
  counterA: `0--- | 0--- | 0--- | 0- 5 6 | 1'--- | 6- 5- | 3--- ; | 5--- ;`,
  counterB: `3--- | 5--- | 6- 5- | 3--- ; | 1--- | 2- 3- | 2--- | 1--- ;`,
  rootsA: `1, 6,, 2, 5,, 3, 1, 6,, 1,`,
  rootsB: `5,, 6,, 2, 5,, 6,, 3, 5,, 1,`,
  pattern: [0, 2, 4, 5, 4, 2, 4, 2],
  bed: 'h',
});

// 5. ยอดหมอกระฆังเย็น: sunset on the misty peak; the qin answers low, a bowl rings.
lofi({
  id: 'lofiPeak',
  order: 64,
  title: 'ระฆังเย็นบนยอดหมอก',
  mood: 'ลึก นิ่ง เย็นย่ำ',
  key: 'C',
  mode: 'shang',
  bpm: 56,
  seed: 6005,
  counter: 'qin',
  leadA: `2g 3_ 5_ 6~- | 5_ 3_ 2 1\\- | 2 5 3_ 2_ 1 | 2%--- | 5_ 6_ 1' 2' 1' | 6- 5 6/ | 5_ 3_ 2_ 1_ 6, 1 | 2~---`,
  leadB: `6,g 1 2 3 | 5^- 3 2 | 1_ 2_ 3 2_ 1_ 6, | 5,%--- | 6, 2 1 6,\\ | 5,- 6, 1 | 2_ 3_ 5 3 2/ | 2~---`,
  counterA: `0--- | 0--- | 6,- 5,- | 2,--- | 0--- | 0--- | 1,- 6,,- | 2,---`,
  counterB: `2,--- | 5,,--- | 1,- 2,- | 5,,--- | 6,,--- | 2,- 1,- | 6,,--- | 2,---`,
  rootsA: `2, 6,, 2, 5,, 1, 6,, 1, 2,`,
  rootsB: `6,, 5,, 1, 5,, 6,, 2, 1, 2,`,
  pattern: [0, 3, 5, 3, 2, 3, 5, 3],
  bed: 'b',
});

// 6. ฝนพรำหลังคาไม้ไผ่: rain on a bamboo roof; erhu answers.
lofi({
  id: 'lofiRain',
  order: 65,
  title: 'ฝนพรำหลังคาไม้ไผ่',
  mood: 'เหงา ๆ อุ่น ๆ ฝนตก',
  key: 'F',
  mode: 'yu',
  bpm: 62,
  seed: 6006,
  counter: 'erhu',
  leadA: `3g 6_ 5_ 3- | 2_ 3_ 1 6,\\- | 1 2 3_ 5_ 6 | 5%--- | 6_ 1'_ 6 5 3 | 2. 3_ 5 3~ | 2_ 1_ 6,_ 5,_ 6, 1/ | 6,~---`,
  leadB: `6G- 1' 6 | 5_ 3_ 5 6^- | 3 2_ 3_ 1 2 | 3%--- | 5 3 2 1\\ | 6,- 1 2/ | 3_ 2_ 1 6,_ 5,_ 6, | 6,~---`,
  counterA: `0--- | 0--- | 0--- | 0- 3 5 | 6--- | 5- 3- | 2--- ; | 6,--- ;`,
  counterB: `1--- | 6,--- | 3- 2- | 1--- ; | 6,--- | 5,- 6,- | 1--- | 6,--- ;`,
  rootsA: `6,, 3,, 1, 5,, 6,, 2, 6,, 6,,`,
  rootsB: `1, 5,, 3,, 6,, 2, 1, 5,, 6,,`,
  pattern: [0, 2, 4, 2, 3, 2, 4, 2],
  double: 'qin',
});

/** The station's tracks, in playing order. */
export const LOFI_TRACKS = [
  'lofiLake',
  'lofiFalls',
  'lofiLantern',
  'lofiBlossom',
  'lofiPeak',
  'lofiRain',
] as const;
